import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, ChevronDown, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CONTACT, DESTINATIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-forest/25 bg-transparent py-3 text-sm text-ink placeholder:text-forest-mist/50 outline-none transition-colors duration-300 focus:border-gold";

const labelCls = "text-[10px] uppercase tracking-widest2 font-medium text-forest";

function Field({ label, children, testId }) {
  return (
    <div>
      <label className={labelCls} htmlFor={testId}>
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const selectCls = `${inputCls} appearance-none pr-8 cursor-pointer`;

export default function Enquiry({ prefillDestination }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    travel_dates: "",
    travellers: "",
    budget: "",
    holiday_type: "",
    special_requirements: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (prefillDestination) {
      setForm((f) => ({ ...f, destination: prefillDestination }));
    }
  }, [prefillDestination]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await new Promise((r) => setTimeout(r, 600));
      await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).then((res) => {
        if (!res.ok) throw new Error("failed");
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      destination: "",
      travel_dates: "",
      travellers: "",
      budget: "",
      holiday_type: "",
      special_requirements: "",
      message: "",
    });
    setStatus("idle");
  };

  return (
    <section
      id="plan-your-journey"
      data-testid="enquiry-section"
      className="border-t border-sand bg-cream py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Eyebrow>Plan Your Journey</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 text-4xl leading-[1.15] text-forest md:text-5xl">
              Begin with a <span className="italic text-gold">conversation.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-[15px] leading-relaxed text-forest-mist">
              Tell us a little about the tailor-made holiday you are imagining — the destination, the season, the
              feeling. Pritha will reply personally to begin shaping a journey that is entirely yours.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 space-y-5 border-t border-gold/40 pt-8">
              <a
                data-testid="enquiry-email-link"
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-4 text-sm text-forest transition-colors hover:text-gold"
              >
                <Mail className="h-4 w-4 text-gold" strokeWidth={1.5} />
                {CONTACT.email}
              </a>
              <p className="flex items-center gap-4 text-sm text-forest">
                <MapPin className="h-4 w-4 text-gold" strokeWidth={1.5} />
                {CONTACT.location}
              </p>
              <p className="pt-2 text-xs leading-relaxed text-forest-mist">
                {CONTACT.founder} · {CONTACT.role}
                <br />
                Personal replies to every enquiry.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:col-start-6" data-testid="enquiry-form-wrapper">
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid="enquiry-success"
              className="flex h-full flex-col items-start justify-center border border-gold/40 bg-ivory p-10 md:p-14"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-gold text-gold">
                <Check className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <h3 className="mt-7 font-display text-3xl text-forest md:text-4xl">
                Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-forest-mist">
                Your journey enquiry has been received. Pritha will reply to you personally, and the planning begins —
                no obligation, no templates, just a conversation about where you would like to go.
              </p>
              <button
                data-testid="enquiry-reset-button"
                onClick={reset}
                className="mt-8 border border-forest/30 px-8 py-3.5 text-[11px] uppercase tracking-widest2 font-medium text-forest transition-colors duration-500 hover:bg-forest hover:text-ivory"
              >
                Plan Another Journey
              </button>
            </motion.div>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="enquiry-form" className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2">
                <Field label="Name" testId="enquiry-name">
                  <input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" testId="enquiry-email">
                  <input
                    id="enquiry-email"
                    data-testid="enquiry-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="WhatsApp / Phone" testId="enquiry-phone">
                  <input
                    id="enquiry-phone"
                    data-testid="enquiry-phone-input"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className={inputCls}
                  />
                </Field>
                <Field label="Destination of Interest" testId="enquiry-destination">
                  <div className="relative">
                    <select
                      id="enquiry-destination"
                      data-testid="enquiry-destination-select"
                      required
                      value={form.destination}
                      onChange={set("destination")}
                      className={selectCls}
                    >
                      <option value="" disabled>
                        Select a destination
                      </option>
                      {DESTINATIONS.map((d) => (
                        <option key={d.slug} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                      <option value="Somewhere else">Somewhere else / Not sure yet</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
                  </div>
                </Field>
                <Field label="Travel Dates" testId="enquiry-dates">
                  <input
                    id="enquiry-dates"
                    data-testid="enquiry-dates-input"
                    required
                    value={form.travel_dates}
                    onChange={set("travel_dates")}
                    placeholder="e.g. December 2025, flexible"
                    className={inputCls}
                  />
                </Field>
                <Field label="Number of Travellers" testId="enquiry-travellers">
                  <div className="relative">
                    <select
                      id="enquiry-travellers"
                      data-testid="enquiry-travellers-select"
                      required
                      value={form.travellers}
                      onChange={set("travellers")}
                      className={selectCls}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {["1–2 Travellers", "3–4 Travellers", "5–8 Travellers", "9+ (Group / Celebration)"].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
                  </div>
                </Field>
                <Field label="Approximate Budget" testId="enquiry-budget">
                  <div className="relative">
                    <select
                      id="enquiry-budget"
                      data-testid="enquiry-budget-select"
                      required
                      value={form.budget}
                      onChange={set("budget")}
                      className={selectCls}
                    >
                      <option value="" disabled>
                        Select a range
                      </option>
                      {[
                        "Under ₹2,00,000",
                        "₹2,00,000 – ₹5,00,000",
                        "₹5,00,000 – ₹10,00,000",
                        "Above ₹10,00,000",
                        "Prefer to discuss",
                      ].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
                  </div>
                </Field>
                <Field label="Type of Journey" testId="enquiry-type">
                  <div className="relative">
                    <select
                      id="enquiry-type"
                      data-testid="enquiry-type-select"
                      required
                      value={form.holiday_type}
                      onChange={set("holiday_type")}
                      className={selectCls}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {[
                        "Romantic Escape / Honeymoon",
                        "Family Holiday",
                        "Special Occasion / Celebration",
                        "Luxury & Premium Journey",
                        "Cultural Exploration",
                        "Slow & Immersive Travel",
                        "Something Else",
                      ].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
                  </div>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Special Requirements" testId="enquiry-requirements">
                    <input
                      id="enquiry-requirements"
                      data-testid="enquiry-requirements-input"
                      value={form.special_requirements}
                      onChange={set("special_requirements")}
                      placeholder="Dietary needs, accessibility, celebrations, anything at all"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message" testId="enquiry-message">
                    <textarea
                      id="enquiry-message"
                      data-testid="enquiry-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about the journey you are imagining — the places, the pace, the people..."
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>
                {status === "error" && (
                  <p data-testid="enquiry-error" className="text-xs text-red-700 sm:col-span-2">
                    Something went wrong sending your enquiry. Please email us directly at {CONTACT.email}.
                  </p>
                )}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="enquiry-submit-button"
                    disabled={status === "sending"}
                    className="w-full bg-forest px-12 py-4 text-[11px] uppercase tracking-widest2 font-medium text-ivory transition-colors duration-500 hover:bg-gold hover:text-forest disabled:opacity-60 sm:w-auto"
                  >
                    {status === "sending" ? "Sending..." : "Start Planning"}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
